import React, { useEffect, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import type { Message } from '../../services/api';
import { useAv2Colors } from '../../theme/hooks';
import {
  executeAction,
  MessageVote,
} from '../../services/api';
import ThinkingIndicator from '../ThinkingIndicator';
import SkeletonLoader from '../SkeletonLoader';
import type { StreamStatus } from '../../hooks/useConversation';
import { composeClarificationMessage, extractClarificationFieldValues } from '../../utils/composeClarificationMessage';
import {
  buildClarificationSummaryText,
  replaceClarificationBlocksWithSummary,
} from '../../utils/clarificationSubmittedUi';
import type { FollowupMeta } from '../../hooks/useConversation';
import MessageActionsBar from '../MessageActionsBar';

/** Global event: the av2 container listens for this to open the Business Profile panel. */
export const AV_PROFILE_OPEN_EVENT = 'askvantage:profile:open';

export interface ProfileOpenDetail {
  mode?: 'business_parameters' | 'kpi_targets';
  category?: string;
  key?: string;
  metric_key?: string;
  period_type?: string;
  location_ids?: number[];
  location_name?: string;
}

interface Props {
  message: Message;
  streamStatus?: StreamStatus;
  streamingContainerRef?: React.RefObject<HTMLDivElement>;
  /** When set (e.g. latest assistant message), parent scrolls this to top when the answer is ready */
  topAnchorRef?: React.Ref<HTMLDivElement>;
  isStreaming?: boolean;
  onFollowup?: (question: string, meta?: FollowupMeta) => void | Promise<void>;
  onVantageLink?: (entity: string, params: Record<string, string>) => void;
  onBuyTokens?: () => void;
  onVote?: (messageId: string | number, vote: MessageVote) => void;
  conversationId: number | null;
}

const AskVantageResponse: React.FC<Props> = ({
  message,
  streamStatus,
  streamingContainerRef,
  topAnchorRef,
  isStreaming = false,
  onFollowup,
  onVantageLink,
  onBuyTokens,
  onVote,
  conversationId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const av2 = useAv2Colors();

  // Execute <script> tags inside LLM-generated HTML (e.g. Chart.js).
  // dangerouslySetInnerHTML renders HTML but does not run scripts — we
  // clone each <script> so the browser treats it as newly inserted.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !message.html_content) return;

    const scripts = el.querySelectorAll<HTMLScriptElement>('script');
    scripts.forEach((original) => {
      if (original.src) return;
      const clone = document.createElement('script');
      clone.textContent = original.textContent;
      original.parentNode?.replaceChild(clone, original);
    });
  }, [message.id, message.html_content]);

  // Replace clarification form with persisted summary when `ui_state` says submitted (reload / reopen).
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const summary = buildClarificationSummaryText(message.ui_state);
    if (!summary) return;
    replaceClarificationBlocksWithSummary(el, summary);
  }, [message.id, message.html_content, message.ui_state]);

  // Attach event delegation for interactive elements inside streamed HTML
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleClick = async (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const closest = (selector: string) => target.closest<HTMLElement>(selector);

      // Buy tokens button (token limit error card)
      const buyTokensEl = closest('[data-buy-tokens]');
      if (buyTokensEl) {
        e.preventDefault();
        onBuyTokens?.();
        return;
      }

      // Business Profile deep link — LLM renders button; container opens panel
      const profileOpenEl = closest('[data-av-profile-open]');
      if (profileOpenEl) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openBusinessProfileFromElement(profileOpenEl);
        return;
      }

      // Clarification form — LLM renders full UI; React only composes a user message (not a form POST)
      const clarifySubmit = closest('[data-av-clarification-submit]');
      if (clarifySubmit && onFollowup) {
        const root = clarifySubmit.closest<HTMLElement>('[data-av-clarification]');
        if (root) {
          e.preventDefault();
          const btn = clarifySubmit as HTMLButtonElement;
          if (btn.disabled || root.querySelector('.av2-clarification-summary')) return;

          const text = composeClarificationMessage(root);
          if (!text.trim()) return;

          const fieldValues = extractClarificationFieldValues(root);
          const prevLabel = btn.textContent;
          btn.disabled = true;
          btn.textContent = '…';
          void (async () => {
            try {
              await onFollowup(text, {
                assistantMessageId: message.id,
                clarificationPayload: {
                  field_values: fieldValues,
                  composed_preview: text
                }
              });
            } catch {
              btn.disabled = false;
              if (prevLabel) btn.textContent = prevLabel;
            }
          })();
        }
        return;
      }

      // Follow-up chip
      const followupEl = closest('[data-followup]');
      if (followupEl && onFollowup) {
        e.preventDefault();
        const question =
          followupEl.getAttribute('data-question') || followupEl.textContent || '';
        if (question.trim()) onFollowup(question.trim());
        return;
      }

      // Vantage deep-link — navigate to DAR entity or fall back to follow-up
      const linkEl = closest('[data-vantage-link]');
      if (linkEl) {
        e.preventDefault();
        const entity = linkEl.getAttribute('data-entity') ?? '';
        let params: Record<string, string> = {};
        try {
          params = JSON.parse(linkEl.getAttribute('data-params') ?? '{}');
        } catch {
          // ignore
        }
        params._label = linkEl.textContent?.trim() ?? '';
        if (onVantageLink) {
          onVantageLink(entity, params);
        }
        return;
      }

      // Action card confirm button
      const actionCardEl = closest('[data-action-card]');
      if (actionCardEl) {
        const confirmBtn = target.closest<HTMLElement>('[data-action-confirm]');
        if (confirmBtn && conversationId) {
          e.preventDefault();
          const profileDetail = businessConfigDetailFromActionCard(actionCardEl);
          if (profileDetail) {
            e.stopPropagation();
            e.stopImmediatePropagation();
            openBusinessProfile(profileDetail);
            return;
          }

          const capability = actionCardEl.getAttribute('data-capability') ?? '';
          let params: Record<string, unknown> = {};
          try {
            params = JSON.parse(actionCardEl.getAttribute('data-params') ?? '{}');
          } catch {
            // ignore
          }
          confirmBtn.textContent = 'Processing...';
          (confirmBtn as HTMLButtonElement).disabled = true;
          try {
            await executeAction(conversationId, capability, params);
            confirmBtn.textContent = 'Done ✓';
          } catch {
            confirmBtn.textContent = 'Failed — try again';
            (confirmBtn as HTMLButtonElement).disabled = false;
          }
        }
        return;
      }
    };

    el.addEventListener('click', handleClick, true);
    return () => el.removeEventListener('click', handleClick, true);
  }, [onFollowup, onVantageLink, onBuyTokens, conversationId, message.id, message.ui_state]);

  const chipStyle: React.CSSProperties = {
    fontSize: '12px',
    padding: '5px 12px',
    borderRadius: '20px',
    border: `1px solid ${av2.primaryBorder}`,
    background: av2.primaryTintBg,
    color: av2.accentText,
    cursor: 'pointer',
  };

  // For completed V2 messages, render stored HTML
  if (!isStreaming) {
    if (message.html_content) {
      return (
        <ResponseWrapper ref={topAnchorRef}>
          <div
            ref={containerRef}
            className="av2-response"
            dangerouslySetInnerHTML={{ __html: message.html_content }}
          />
          <MessageActionsBar
            messageId={message.id}
            onCopy={() => {
              const text = containerRef.current?.innerText ?? message.content ?? '';
              navigator.clipboard.writeText(text).catch(() => {});
            }}
            vote={message.vote}
            onVote={onVote ? (vote) => onVote(message.id, vote) : undefined}
          />
        </ResponseWrapper>
      );
    }
    return (
      <ResponseWrapper ref={topAnchorRef}>
        <div
          style={{
            borderLeft: `3px solid ${av2.errorBorder}`,
            padding: '8px 12px',
            background: av2.errorBg,
            borderRadius: '0 6px 6px 0',
            margin: '8px 0',
            fontSize: '13px',
            color: av2.accentTextLight,
          }}
        >
          Something went wrong generating a response. Please try again.
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button
            data-followup
            data-question="Try again"
            className="av2-chip"
            style={chipStyle}
            onClick={() => onFollowup?.('Try again')}
          >
            Try again
          </button>
        </div>
      </ResponseWrapper>
    );
  }

  // For currently-streaming message: delegate to the streaming container
  if (isStreaming) {
    return (
      <ResponseWrapper ref={topAnchorRef}>
        {streamStatus === 'thinking' && <ThinkingIndicator />}
        {streamStatus === 'thinking' && <SkeletonLoader />}
        <StreamingDiv ref={streamingContainerRef} className="av2-response" />
      </ResponseWrapper>
    );
  }

  return null;
};

function openBusinessProfileFromElement(primary: HTMLElement): void {
  openBusinessProfile(profileDetailFromElement(primary));
}

function openBusinessProfile(detail: ProfileOpenDetail): void {
  window.dispatchEvent(new CustomEvent(AV_PROFILE_OPEN_EVENT, {
    detail
  }));
}

function profileDetailFromElement(primary: HTMLElement): ProfileOpenDetail {
  const attr = (name: string) => primary.getAttribute(name) || undefined;
  const rawLocationIds = attr('data-av-profile-location-ids') || '';
  const locationIds = Array.isArray(rawLocationIds)
    ? rawLocationIds.map(Number).filter(Number.isFinite)
    : String(rawLocationIds).split(',').map((id) => Number(id.trim())).filter(Number.isFinite);

  return {
    mode: attr('data-av-profile-mode') as ProfileOpenDetail['mode'],
    category: attr('data-av-profile-category'),
    key: attr('data-av-profile-key'),
    metric_key: attr('data-av-profile-metric-key'),
    period_type: attr('data-av-profile-period-type'),
    ...(locationIds.length > 0 ? { location_ids: locationIds } : {}),
  };
}

function businessConfigDetailFromActionCard(card: HTMLElement): ProfileOpenDetail | null {
  const capability = (card.getAttribute('data-capability') || '').toLowerCase();
  const text = (card.textContent || '').toLowerCase();
  const params = parseJsonObject(card.getAttribute('data-params'));
  const paramText = JSON.stringify(params).toLowerCase();
  const haystack = `${capability} ${text} ${paramText}`;

  const isBusinessConfig =
    haystack.includes('business parameter') ||
    haystack.includes('business_parameter') ||
    haystack.includes('kpi') ||
    haystack.includes('monthly_rent') ||
    haystack.includes('monthly rent') ||
    haystack.includes('rent');

  if (!isBusinessConfig) return null;

  const locationIds = locationIdsFromParams(params);
  const locationName = stringParam(params, 'location_name') ||
    stringParam(params, 'locationName') ||
    locationNameFromText(card.textContent || '');
  const metricKey = stringParam(params, 'metric_key') || stringParam(params, 'metricKey');
  const key = stringParam(params, 'key') ||
    stringParam(params, 'parameter_key') ||
    (haystack.includes('monthly_rent') || haystack.includes('monthly rent') ? 'monthly_rent' : undefined);

  if (metricKey) {
    return {
      mode: 'kpi_targets',
      category: stringParam(params, 'category'),
      metric_key: metricKey,
      period_type: stringParam(params, 'period_type') || stringParam(params, 'periodType'),
      ...(locationIds.length > 0 ? { location_ids: locationIds } : {}),
      ...(locationName ? { location_name: locationName } : {}),
    };
  }

  return {
    mode: 'business_parameters',
    category: stringParam(params, 'category') || (key === 'monthly_rent' ? 'location_details' : undefined),
    key,
    ...(locationIds.length > 0 ? { location_ids: locationIds } : {}),
    ...(locationName ? { location_name: locationName } : {}),
  };
}

function parseJsonObject(raw: string | null): Record<string, unknown> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function stringParam(params: Record<string, unknown>, key: string): string | undefined {
  const value = params[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function locationIdsFromParams(params: Record<string, unknown>): number[] {
  const raw = params.location_ids ?? params.location_id ?? params.locationIds ?? params.locationId;
  return Array.isArray(raw)
    ? raw.map(Number).filter(Number.isFinite)
    : String(raw || '').split(',').map((id) => Number(id.trim())).filter(Number.isFinite);
}

function locationNameFromText(text: string): string | undefined {
  const normalized = text.replace(/\s+/g, ' ').trim();
  const match = normalized.match(/\bfor\s+(.+?)(?:\.|\s+This value|\s+Open\b|\s+Confirm\b|$)/i);
  return match?.[1]?.trim();
}

export default AskVantageResponse;

const ResponseWrapper = styled.div`
  padding: 4px 0 16px;

  .av2-response .av2-clarification-summary {
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme.av2.textSecondary};
    margin: 0;
    padding: 12px 14px;
    background: ${({ theme }) => theme.av2.surfaceElevated};
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.av2.border};
  }
`;

const StreamingDiv = styled.div`
  min-height: 20px;
`;
